import * as ents from '@attain-apps/entities'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import type { Plugin } from './plugin'

// TODO: remove
;(Error as any).stackTraceLimit = Infinity

/**
 * When in the middle of many pages, we show some of the pages before and after
 * the current page (i.e. neighbor pages), then we show ... and then the first
 * and last page. This determines the maximum number of neighbor pages to show
 * between the ellipses and current page, both for previous and next pages.
 */
const MAX_NEIGHBOR_PAGES_TO_SHOW = 2

export class Selectors<TTypeParams extends ec.models.TypeParams> {
  /* ------------------------------- Pagination ------------------------------- */
  /** TODO: documentation */
  public readonly pagination
  /** TODO: documentation */
  public readonly paginationQuantity
  /** TODO: documentation */
  public readonly paginationSkip
  /** TODO: documentation */
  public readonly paginationSelectedPageNumber
  /** Following pages to display in pagination */
  public readonly paginationNextPagesToNavigate
  /** Previous pages to display in pagination */
  public readonly paginationPreviousPagesToNavigate
  /** TODO: documentation */
  public readonly paginationDisplayedRange
  /** TODO: documentation */
  public readonly paginationHasPreviousPage
  /** Shows total number of pages */
  public readonly paginationNumberOfPages
  /**
   * Indicates whether a next page is available.
   *
   * NOTE: When the {@link ec.models.Api | Api} does not provide a total count, the presence of
   * a next page is determined by comparing the number of returned entities to
   * the requested quantity. If fewer entities are returned than requested, it
   * is assumed there is no next page.
   *
   * There is an "edge case" when the number of returned entities is the same as
   * requested quantity.
   * In that case it is assumed that there is a next page, but at some point
   * those may be the last x entities and it just happens to be the same number
   * as requested quantity.
   * The expected behavior is to navigate to next page and present empty state.
   */
  public readonly paginationHasNextPage
  /**
   * Indicates whether input for the current page is disabled.
   *
   */
  public readonly paginationIsPageInputDisabled
  /** TODO: documentation */
  public readonly hasData

  constructor(plugin: Plugin<TTypeParams>) {
    this.pagination =
      plugin.collection.plugins.currentListPlugin.feature.selectors.queryRequest.project(
        queryRequest => queryRequest.pagination,
      )
    this.paginationQuantity = this.pagination.project(
      pagination => pagination.quantity,
    )
    this.paginationSkip = this.pagination.project(pagination => pagination.skip)
    this.paginationSelectedPageNumber = this.paginationQuantity.project(
      this.paginationSkip.asSelector,
      (quantity, skip) =>
        ents.core.common.Request.For.Pagination.ByPosition.getPageNumber({
          quantity,
          skip,
        }),
    )
    this.paginationNumberOfPages =
      plugin.collection.plugins.currentListPlugin.feature.selectors.matchedCount.project(
        this.paginationQuantity.asSelector,
        (matchedCount, quantity) =>
          // NOTE: there must always be at least one page
          matchedCount === undefined || quantity === undefined
            ? 1
            : Math.max(Math.ceil(matchedCount / quantity), 1),
      )

    this.paginationIsPageInputDisabled = this.paginationNumberOfPages.project(
      numberOfPages =>
        // We can't really know when to disabled page input when there is no
        // count so we want to leave it enabled. If we input page that has no
        // data we will end up with empty state which is expected behavior
        plugin.collection.plugins.apiExecutionPlugin.canExecute('count') &&
        numberOfPages === 1,
    )

    this.paginationNextPagesToNavigate =
      this.paginationSelectedPageNumber.project(
        this.paginationNumberOfPages.asSelector,
        (selectedPageNumber, numberOfPages) => {
          if (numberOfPages <= selectedPageNumber) {
            // If there are no pages (or negative pages, which would otherwise
            // throw an error) after this page, then we just return an empty
            // list
            return []
          }
          // Example: max of 2 neighbors and the number of pages is 10; if the
          // current page is any of 7 - 10, we don't need ellipses. On page 7,
          // we show the 2 neighbors, 8 & 9, then we show the last page, 10.
          // There is no gap between 9 and 10, so no ellipses needed
          if (
            numberOfPages - selectedPageNumber <=
            MAX_NEIGHBOR_PAGES_TO_SHOW + 1
          ) {
            // If we are close enough to the beginning to not need ellipses,
            // then we just show all the pages leading up to the selected page
            return Array.from(
              { length: numberOfPages - selectedPageNumber },
              (_, i) => selectedPageNumber + i + 1,
            )
          }
          // Otherwise:
          return [
            // Next neighbor pages
            ...Array.from(
              { length: MAX_NEIGHBOR_PAGES_TO_SHOW },
              (_, i) => selectedPageNumber + i + 1,
            ),
            // Ellipses
            undefined,
            // Last page
            numberOfPages,
          ]
        },
      )
    this.paginationPreviousPagesToNavigate =
      this.paginationSelectedPageNumber.project(selectedPageNumber => {
        // If the count method is not implemented, we want to navigate through
        // pagination using arrows and not have any previous numbers show, only
        // current page number
        if (!plugin.collection.plugins.apiExecutionPlugin.canExecute('count')) {
          return []
        }
        // Example: max of 2 neighbors; if the current page is any of 1 - 4,
        // we don't need ellipses. On page 4, we show the 2 neighbors, 2 & 3,
        // then we show the first page, 1. There is no gap between 1 and 2, so
        // no ellipses needed
        if (selectedPageNumber <= MAX_NEIGHBOR_PAGES_TO_SHOW + 2) {
          // If we are close enough to the beginning to not need ellipses,
          // then we just show all the pages leading up to the selected page
          return Array.from({ length: selectedPageNumber - 1 }, (_, i) => i + 1)
        }
        // Otherwise:
        return [
          // First page
          1,
          // Ellipses
          undefined,
          // Previous neighbor pages
          ...Array.from(
            { length: MAX_NEIGHBOR_PAGES_TO_SHOW },
            (_, i) => selectedPageNumber - MAX_NEIGHBOR_PAGES_TO_SHOW + i,
          ),
        ]
      })

    this.paginationDisplayedRange =
      plugin.collection.plugins.currentListPlugin.feature.selectors.matchedCount.project(
        this.paginationQuantity.asSelector,
        this.paginationSkip.asSelector,
        (matchedCount, quantity, skip) =>
          // If there isn't count implemented, and there aren't quantity and
          // matchedCount we don't have pagination, otherwise we show
          // range information. If skip is undefined we say that we are on the
          // first page.
          plugin.collection.plugins.apiExecutionPlugin.canExecute('count') &&
          quantity &&
          matchedCount
            ? // We want to show (skip + 1) - (skip + quantity) / matchedCount if
              // matchedCount is greater than (skip + quantity). Otherwise we show
              // (skip + 1) - matchedCount / matchedCount, because there is only one
              // page. This is because we want to avoid examples like 1 - 20 / 8
              `${(skip ?? 0) + 1} - ${(skip ?? 0) + quantity < matchedCount ? (skip ?? 0) + quantity : matchedCount} / ${matchedCount}`
            : '',
      )
    this.paginationHasPreviousPage = this.paginationSelectedPageNumber.project(
      pageNumber => pageNumber > 1,
    )

    this.paginationHasNextPage = this.paginationSelectedPageNumber.project(
      this.paginationNumberOfPages.asSelector,
      this.paginationQuantity.asSelector,
      plugin.collection.plugins.currentListPlugin.feature.selectors.entities
        .asSelector,
      (pageNumber, numberOfPages, quantity, entities) =>
        // If we don't have total coun't we say there is next page if number of
        // entities returned for the api is greater than quantity
        plugin.collection.plugins.apiExecutionPlugin.canExecute('count')
          ? pageNumber < numberOfPages
          : entities.length >= (quantity ?? 0),
    )

    this.hasData =
      plugin.collection.plugins.currentListPlugin.feature.selectors.ids.project(
        plugin.collection.plugins.attributePlugin.feature.selectors
          .visibleAttributes.asSelector,
        (ids, attributes) => ids.length > 0 && attributes.length > 0,
      )
  }
}
